import api from './api';

export type BlisterPlacement = 'pack' | 'alongside';
export type BlisterSend = 'every_pack' | 'own_schedule' | 'ask_first';
export type BlisterCharge = 'each_time' | 'on_schedule';

/** PRX-127: the four slots of the blister chart, in card order. */
export const BLISTER_SLOT_KEYS = ['breakfast', 'noon', 'supper', 'bed'] as const;
export type BlisterSlotKey = (typeof BLISTER_SLOT_KEYS)[number];
/** Units per slot. Quarter units, 0 to 20; the server refuses anything else. */
export type BlisterSlots = Record<BlisterSlotKey, number>;

/**
 * PRX-103 part 2: the drug is on the patient's manual billing list. Who and
 * when are stamped by the server; `addedByName` is absent when the name could
 * not be looked up.
 */
export interface BlisterManualBilling {
  addedAt: string;
  addedBy: string;
  addedByName?: string;
}

/**
 * The stored settings as the grid renders them.
 *
 * `send` is the one field the read may present as null. `isPrn` is the
 * authoritative scheduling field, so a row whose stored rule disagrees with it
 * arrives with no rule and `sendMismatch: true` — see the row below.
 */
export interface BlisterGridSettings {
  placement: BlisterPlacement;
  cadenceDays?: number;
  send: BlisterSend | null;
  charge: BlisterCharge;
  ownNextDueDate?: string;
  /** PRX-127: absent means nobody has entered this drug's slots. */
  slots?: BlisterSlots;
  /** PRX-103 part 2: present only while the drug is on the manual billing list. */
  manualBilling?: BlisterManualBilling;
  source: 'fillware' | 'manual';
  updatedAt?: string;
  updatedBy?: string;
}

/** One grid row, as the API serves it. Every date is a plain string. */
export interface BlisterGridRow {
  billingItemId: string;
  /** The exact position in that basket's medications array — the write pin. */
  medicationIndex: number;
  rxNumber: string;
  din: string | null;
  medicationName: string;
  daysSupply: number | null;
  /** The computed working date the billing queue reads. Read-only in PRX-47. */
  nextDueDate: string | null;
  isPrn: boolean;
  /**
   * The drug is on the plan but paused: it keeps its settings and is excluded
   * from the follow-up date exactly like a PRN drug. Still listed, because the
   * settings belong to the drug rather than to the pause.
   */
  paused: boolean;
  /**
   * PRX-66: WHY the drug is paused, when somebody recorded a reason. The value
   * `Blister pack change` means this pack-change workflow is holding it, which
   * is what the tab's held label and the dialog's resume both key off.
   */
  pauseReason: string | null;
  /** PRX-66: the date a paused drug comes back, when one was recorded. */
  pausedUntil: string | null;
  /** Null means nobody has set this drug's rules. */
  blister: BlisterGridSettings | null;
  /** PRX-127: the slot chart's drug columns, as the basket line carries them. */
  brandName?: string | null;
  genericName?: string | null;
  strength?: string | null;
  /** The prescription's sig. */
  directions?: string | null;
  /**
   * PRX-200: the day this Rx was last filled, from Fillware (YYYY-MM-DD). Null
   * when Fillware did not answer for it; absent away from Fillware.
   */
  lastFillDate?: string | null;
  /**
   * PRX-200: where strength and directions came from on the GET at Fillware:
   * `fillware`, or `order` (Fillware did not answer for this Rx, or could not
   * be read). Null or absent elsewhere.
   */
  detailsSource?: 'fillware' | 'order' | null;
  /** PRX-200: whether Fillware's kept fill for this Rx may be dispensed. */
  fillwareDispensable?: boolean | null;
  /**
   * True when the stored send rule said `ask_first` but the drug is no longer
   * PRN. The grid shows "Send rule not set" and staff pick one.
   */
  sendMismatch: boolean;
}

export interface BlisterMedicationsResponse {
  blisterPack: boolean;
  cycleDays: number | null;
  rows: BlisterGridRow[];
  /** Fillware pharmacies only — the seed has nowhere else to come from. */
  seedAvailable: boolean;
  /** True when at least one live drug already carries settings. */
  seeded: boolean;
  /**
   * WP-18: on the answer to a seed only, what the slot pre-fill from Fillware
   * did. Counts and reason codes; absent on every other read.
   */
  slotSeed?: BlisterSlotSeedSummary;
  /**
   * PRX-200: at Fillware, for a patient on packs, whether the per-Rx Fillware
   * read answered. `unavailable` is a failed read. Absent elsewhere.
   */
  fillwareRx?: 'ok' | 'unavailable';
}

/** WP-18 (PRX-127 Q4) — the slot pre-fill, as the server counted it. */
export interface BlisterSlotSeedSummary {
  /** In-pack drugs that had no slots. */
  considered: number;
  filled: number;
  /** In-pack drugs that already had slots; never touched. */
  alreadySet: number;
  /** Left without slots, by reason code. */
  skipped: Record<string, number>;
  /** The server's words for each reason code in `skipped`. The only copy. */
  skippedWords?: Record<string, string>;
}

/** Only the keys the user changed. `null` clears the two optional ones. */
export interface UpdateBlisterMedicationPayload {
  placement?: BlisterPlacement;
  cadenceDays?: number | null;
  send?: BlisterSend;
  charge?: BlisterCharge;
  ownNextDueDate?: string | null;
  /** PRX-127: all four slots together, or null to clear them. */
  slots?: BlisterSlots | null;
  /** The exact line the grid rendered — the server refuses a stale pin. */
  billingItemId: string;
  medicationIndex: number;
}

/** The exact line the grid rendered, for a manual billing change. */
export interface ManualBillingPin {
  billingItemId: string;
  medicationIndex: number;
}

/**
 * PRX-47 — the three per-drug routes, and PRX-103's manual billing pair.
 *
 * The pharmacy is in the path on all of them, and the server answers 404 for a
 * patient at another store. Nothing here substitutes a default when a call
 * fails: the error reaches TanStack Query and the row shows what the server
 * said.
 *
 * All three answer with the whole refreshed grid, so a save never needs a
 * follow-up GET.
 */
/** Every path segment encoded (PRX-137), so no id can address another route. */
const blisterBase = (pharmacyId: string, patientId: string): string =>
  `/api/patients/${encodeURIComponent(pharmacyId)}/${encodeURIComponent(patientId)}/blister`;

const patientBlisterMedicationsService = {
  async list(pharmacyId: string, patientId: string): Promise<BlisterMedicationsResponse> {
    const response = await api.get<BlisterMedicationsResponse>(
      `${blisterBase(pharmacyId, patientId)}/medications`,
    );
    return response.data;
  },

  async update(
    pharmacyId: string,
    patientId: string,
    rxNumber: string,
    payload: UpdateBlisterMedicationPayload,
  ): Promise<BlisterMedicationsResponse> {
    const response = await api.patch<BlisterMedicationsResponse>(
      `${blisterBase(pharmacyId, patientId)}/medications/${encodeURIComponent(rxNumber)}`,
      payload,
    );
    return response.data;
  },

  /** PRX-103 part 2: put the Rx on the manual billing list (`true`) or take it off. */
  async setManualBilling(
    pharmacyId: string,
    patientId: string,
    rxNumber: string,
    onList: boolean,
    pin: ManualBillingPin,
  ): Promise<BlisterMedicationsResponse> {
    const url = `${blisterBase(pharmacyId, patientId)}/manual-billing/${encodeURIComponent(rxNumber)}`;
    const response = onList
      ? await api.put<BlisterMedicationsResponse>(url, undefined, { params: pin })
      : await api.delete<BlisterMedicationsResponse>(url, { params: pin });
    return response.data;
  },

  async seed(
    pharmacyId: string,
    patientId: string,
    force = false,
    /** WP-18: fill slots only; no placement is seeded. Never sent with force. */
    slotsOnly = false,
  ): Promise<BlisterMedicationsResponse> {
    const response = await api.post<BlisterMedicationsResponse>(
      `${blisterBase(pharmacyId, patientId)}/medications/seed`
      + (slotsOnly ? '?slotsOnly=true' : force ? '?force=true' : ''),
    );
    return response.data;
  },
};

export default patientBlisterMedicationsService;
